import React, { useRef } from 'react';
import { vh, vw } from '@/common/style';
import { Animated, View, Easing } from 'react-native';
import { useShowCart } from './useShowCart';
import Image from '@kds/image';

interface AddToCartAnimationProps {
    showAnimation?: boolean;
    cartIconRef?: React.RefObject<View>;
    initialImagePosition?: {
        x?: number;
        y?: number;
    };
}

export function useAddToCartAnimation({ cartIconRef, initialImagePosition = {}, showAnimation }: AddToCartAnimationProps = {}) {
    const imageScale = useRef(new Animated.Value(1));
    const imageOpacity = useRef(new Animated.Value(0));
    const imagePositionX = useRef(new Animated.Value(initialImagePosition.x ?? 0));
    const imagePositionY = useRef(new Animated.Value(initialImagePosition.y ?? 0));
    const cartIconScale = useRef(new Animated.Value(1));
    const cartIconRotate = useRef(new Animated.Value(0));
    const imageRef = useRef(null);
    const { showCart } = useShowCart();

    const reset = () => {
        imageScale.current.setValue(1);
        imageOpacity.current.setValue(0);
        imagePositionX.current.setValue(initialImagePosition.x ?? 0);
        imagePositionY.current.setValue(initialImagePosition.y ?? 0);
        cartIconScale.current.setValue(1);
        cartIconRotate.current.setValue(0);
    };

    const startAnimation = () => {
        // 实验未命中
        if (!showAnimation || !showCart) {
            return;
        }

        if (!cartIconRef?.current || !imageRef?.current) {
            return;
        }

        // TODO: 不用setTimeout会导致measure不准确
        setTimeout(() => {
            cartIconRef.current.measure((x, y, width, height, px, py) => {
                imageRef.current.measure((imageX, imageY, imageWidth, imageHeight) => {
                    const finishedX = px + width / 2 - imageWidth / 2;
                    const finishedY = py + height / 2 - imageHeight / 2;

                    Animated.sequence([
                        Animated.parallel([
                            Animated.timing(imagePositionX.current, {
                                toValue: finishedX,
                                easing: Easing.bezier(0.4, 0, 1, 0.6),
                                duration: 420,
                                useNativeDriver: true,
                            }),
                            Animated.timing(imagePositionY.current, {
                                toValue: finishedY,
                                easing: Easing.bezier(0, 0.3, 0.7, 1),
                                duration: 420,
                                useNativeDriver: true,
                            }),
                            Animated.timing(imageScale.current, {
                                toValue: 0,
                                easing: Easing.bezier(0, 0.3, 0.7, 1),
                                duration: 500,
                                useNativeDriver: true,
                            }),
                            Animated.timing(imageOpacity.current, {
                                toValue: 1,
                                // easing: Easing.bezier(0, 0.3, 0.7, 1),
                                duration: 250,
                                useNativeDriver: true,
                            }),
                        ]),
                        Animated.parallel([
                            Animated.timing(cartIconRotate.current, {
                                toValue: -15 / 360,
                                duration: 250,
                                useNativeDriver: true,
                            }),
                            Animated.timing(cartIconScale.current, {
                                toValue: 1.2,
                                duration: 250,
                                useNativeDriver: true,
                            }),
                        ]),
                        Animated.parallel([
                            Animated.timing(cartIconRotate.current, {
                                toValue: 12 / 360,
                                duration: 250,
                                useNativeDriver: true,
                            }),
                            Animated.timing(cartIconScale.current, {
                                toValue: 1,
                                duration: 250,
                                useNativeDriver: true,
                            }),
                        ]),
                        Animated.timing(cartIconRotate.current, {
                            toValue: -10 / 360,
                            duration: 167,
                            useNativeDriver: true,
                        }),
                        Animated.timing(cartIconRotate.current, {
                            toValue: 5 / 360,
                            duration: 125,
                            useNativeDriver: true,
                        }),
                        Animated.timing(cartIconRotate.current, {
                            toValue: 0,
                            duration: 85,
                            useNativeDriver: true,
                        }),
                    ]).start();

                    // 重置动画
                    setTimeout(() => {
                        reset();
                    }, 1500);
                });
            });
        });
    };

    const AddToCartAnimation = ({ image }: { image: Image }) => {
        if (!image) {
            return null;
        }

        return (
            <View
                pointerEvents="none"
                style={{
                    position: 'absolute',
                    width: vw(100),
                    height: vh(100),
                    zIndex: 99,
                }}
            >
                <Animated.View
                    style={{
                        position: 'absolute',
                        transform: [{ translateX: imagePositionX.current }, { translateY: imagePositionY.current }, { scale: imageScale.current }],
                        opacity: imageOpacity.current,
                    }}
                >
                    <Image ref={imageRef} {...image} />
                </Animated.View>
            </View>
        );
    };

    const CartIconAnimation = ({ children }) => {
        const spin = cartIconRotate.current.interpolate({
            inputRange: [0, 1],
            outputRange: ['0deg', '360deg'],
        });

        return (
            <Animated.View
                style={{
                    transform: [{ scale: cartIconScale.current }, { rotate: spin }],
                }}
            >
                {children}
            </Animated.View>
        );
    };

    return {
        startAnimation,
        showCartOptimization: showAnimation,
        AddToCartAnimation,
        CartIconAnimation,
    };
}
